import type { PillVariant } from "~/common/pill";
import { createMatcher } from "~/util/solid";

type Option<T extends string> = { value: T; label: string };

// The account's referral-category catalog (replaces the old fixed specialty enum).
export type ReferralCategoryOption = { id: string; name: string };

// AI-review confidence tier
export type ReferralLikelihood = "high" | "medium" | "low";

export type ReferralIntent = "evaluate_and_treat" | "consult_only";

export const INTENT_OPTIONS: Option<ReferralIntent>[] = [
  { value: "evaluate_and_treat", label: "Evaluate and treat" },
  { value: "consult_only", label: "Consultation only" },
];

export const intentLabel = (value: ReferralIntent | null): string | null =>
  INTENT_OPTIONS.find((option) => option.value === value)?.label ?? null;

export type ReferralUrgency = "routine" | "urgent" | "emergency";

const URGENCY_LABELS: Record<ReferralUrgency, string> = {
  routine: "Routine",
  urgent: "Urgent",
  emergency: "Emergency",
};

export const URGENCY_OPTIONS: Option<ReferralUrgency>[] = [
  { value: "routine", label: "Routine" },
  { value: "urgent", label: "Urgent" },
  { value: "emergency", label: "Emergency" },
];

export const urgencyLabel = (value: ReferralUrgency): string => URGENCY_LABELS[value];

export type ReferralContactMethod = "fax" | "email" | "phone" | "website";

const CONTACT_METHOD_LABELS: Record<ReferralContactMethod, string> = {
  fax: "Fax",
  email: "Email",
  phone: "Phone",
  website: "Website",
};

export const CONTACT_METHOD_OPTIONS: Option<ReferralContactMethod>[] = [
  { value: "fax", label: "Fax" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "website", label: "Website" },
];

export const contactMethodLabel = (value: ReferralContactMethod): string =>
  CONTACT_METHOD_LABELS[value];

// The referral's relationship: inbound (an outside practice sent us the patient), outbound (we sent
// the patient to an outside practice), or internal (one of our offices handed the patient to
// another). Distinct from a category's applicability, which adds "both".
export type ReferralDirection = "inbound" | "outbound" | "internal";

// Narrows a payload that is one of several direction-shaped variants (e.g. inside a <Match>).
export const isDirection = createMatcher("direction");

// Which side of an internal referral an office stands on: it sent the patient, or it receives them.
export type ReferralSide = "referring" | "receiving";

// The worklist a row sits on: what this office received, or what it sent.
export type ReferralSurface = "inbound" | "outbound";

export type ReferralCategoryDirection = "inbound" | "outbound" | "both";

const CATEGORY_DIRECTION_LABELS: Record<ReferralCategoryDirection, string> = {
  inbound: "Inbound",
  outbound: "Outbound",
  both: "Inbound and outbound",
};

export const CATEGORY_DIRECTION_OPTIONS: Option<ReferralCategoryDirection>[] = [
  { value: "both", label: "Inbound and outbound" },
  { value: "inbound", label: "Inbound only" },
  { value: "outbound", label: "Outbound only" },
];

export const categoryDirectionLabel = (value: ReferralCategoryDirection): string =>
  CATEGORY_DIRECTION_LABELS[value];

export type ReferralLetterType =
  /** Inbound: the report sent back to the referrer after the evaluation visit. */
  | "consult_report_back"
  /** Inbound: the report sent back to the referrer after treatment completes. */
  | "completion_report_back"
  /** Outbound: the request letter we send a specialist to refer a patient out. */
  | "referral_request"
  // The appointment provider report ("external letter"): a visit summary to a directory provider.
  | "external_letter";

const LETTER_TYPE_LABELS: Record<ReferralLetterType, string> = {
  consult_report_back: "Consult report",
  completion_report_back: "Completion report",
  referral_request: "Referral request",
  external_letter: "External letter",
};

export const letterTypeLabel = (value: ReferralLetterType): string => LETTER_TYPE_LABELS[value];

// The letter type with its article, for use mid-sentence: "a field a referral request can fill in".
const LETTER_TYPE_NAMES: Record<ReferralLetterType, string> = {
  consult_report_back: "a consult report",
  completion_report_back: "a completion report",
  referral_request: "a referral request",
  external_letter: "an external letter",
};

export const letterTypeName = (value: ReferralLetterType): string => LETTER_TYPE_NAMES[value];

export type ReferralLetterStatus = "drafting" | "draft" | "exporting" | "exported";

const LETTER_STATUS_LABELS: Record<ReferralLetterStatus, string> = {
  drafting: "Drafting",
  draft: "Ready for review",
  exporting: "Exporting",
  exported: "Exported",
};

export const letterStatusLabel = (value: ReferralLetterStatus): string =>
  LETTER_STATUS_LABELS[value];

const LETTER_STATUS_VARIANTS: Record<ReferralLetterStatus, PillVariant> = {
  drafting: "plain",
  draft: "warning",
  exporting: "plain",
  exported: "success",
};

export const letterStatusVariant = (value: ReferralLetterStatus): PillVariant =>
  LETTER_STATUS_VARIANTS[value];

// How the linker attributed a clinical event to the referral
export type ReferralMatchBasis = "code_match" | "consult_code" | "sole_open_referral" | "manual";

const MATCH_BASIS_LABELS: Record<ReferralMatchBasis, string> = {
  code_match: "Matched by procedure",
  consult_code: "Matched as consult",
  sole_open_referral: "Only open referral",
  manual: "Linked manually",
};

export const matchBasisLabel = (value: ReferralMatchBasis): string => MATCH_BASIS_LABELS[value];
